import React, { useState, useEffect } from 'react';
import {
  User,
  Mail,
  Building,
  Phone,
  MapPin,
  Save,
  Edit3,
  Camera,
  Shield,
  CreditCard,
  Bell,
  Globe,
  Lock,
  Check,
  X,
  AlertCircle,
  Loader2,
  Eye,
  EyeOff,
  Crown
} from 'lucide-react';

// Deep-linkable tabs. The send dialog links straight to the tab holding the
// detail it needs (?tab=billing&edit=1), so filling it in is one click rather
// than a hunt through the page.
const TAB_IDS = ['personal', 'company', 'billing', 'security', 'notifications'];

const readTabFromUrl = () => {
  const requested = new URLSearchParams(window.location.search).get('tab');
  return TAB_IDS.includes(requested) ? requested : 'personal';
};

const ProfilePage = ({ onProfileUpdate }) => {
  const [activeTab, setActiveTab] = useState(readTabFromUrl);
  // Arriving with edit=1 means somebody was sent here to fill something in;
  // making them find the Edit button first would be the same obstacle again.
  const [isEditing, setIsEditing] = useState(
    () => new URLSearchParams(window.location.search).get('edit') === '1'
  );
  const [isSaving, setIsSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  // Team account summary from /api/auth/me — set for everyone, but only a
  // sub-user (member / co-admin) gets the reduced profile.
  const [account, setAccount] = useState(null);
  const teamMember = Boolean(account && account.role && account.role !== 'owner');

  // User data state
  const [userData, setUserData] = useState({
    firstName: '',
    lastName: '',
    businessEmail: '',
    companyName: '',
    phone: '',
    country: '',
    zipCode: '',
    billingAddress: '',
    vatId: '', // Only VAT ID for billing
    senderEmail: '',
    plan: 'Free',
    authProvider: 'email'
  });

  // Password change state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Check if user signed up with Google
  const isGoogleUser = userData.authProvider === 'google';

  // Notification settings
   const [notifications, setNotifications] = useState({
    emailNotifications: true,

  });

  

  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState('');
  const [showChangesModal, setShowChangesModal] = useState(false);
  const [pendingChanges, setPendingChanges] = useState([]);
  const [originalData, setOriginalData] = useState({});

  // Fetch user data on component mount
  useEffect(() => {
    fetchUserData();
  }, []);

const fetchUserData = async () => {
  try {
    const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://mars2.mediacle.net'}/api/auth/me`, {
      credentials: 'include'
    });
    
    const data = await response.json();
    
    if (data.success) {
      const newUserData = {
        firstName: data.data.firstName || '',
        lastName: data.data.lastName || '',
        businessEmail: data.data.email || data.data.businessEmail || '',
        companyName: data.data.companyName || '',
        phone: data.data.phone || data.data.phoneNumber || '',
        country: data.data.country || '',
        zipCode: data.data.zipCode || '',
        billingAddress: data.data.billingAddress || '',
        vatId: data.data.vatId || data.data.taxId || '',
        senderEmail: data.data.senderEmail || '',
        plan: data.data.plan || 'Free',
        authProvider: data.data.authProvider || data.data.auth_provider || 'email'
      };

      setUserData(newUserData);
      setAccount(data.data.account || null);
      
      // Store original data for comparison
      setOriginalData(newUserData);
      
      // IMPORTANT: Set the notification state from backend
      // Two ways the backend might send it:
      if (typeof data.data.notifications === 'boolean') {
        // Backend sends boolean directly
        setNotifications({
          emailNotifications: data.data.notifications
        });
      } else if (data.data.notificationSettings) {
        // Backend sends notificationSettings object
        setNotifications({
          emailNotifications: data.data.notificationSettings.emailNotifications ?? true
        });
      } else {
        // Fallback - default to true if not specified
        setNotifications({
          emailNotifications: true
        });
      }

    }
  } catch (error) {
    console.error('Error fetching user data:', error);
  }
};

// Also, update the initial state to null to show loading state:


  const detectChanges = () => {
    const changes = [];
    const fieldLabels = {
      firstName: 'First Name',
      lastName: 'Last Name',
      companyName: 'Company Name',
      phone: 'Phone Number',
      country: 'Country',
      zipCode: 'ZIP Code',
      billingAddress: 'Billing Address',
      vatId: 'VAT ID'
    };

    Object.keys(fieldLabels).forEach(key => {
      if (userData[key] !== originalData[key]) {
        changes.push({
          field: key,
          label: fieldLabels[key],
          oldValue: originalData[key] || 'Not set',
          newValue: userData[key] || 'Not set',
          status: 'pending'
        });
      }
    });

    // Check password changes
    if (activeTab === 'security' && passwordData.currentPassword) {
      changes.push({
        field: 'password',
        label: 'Password',
        oldValue: '••••••••',
        newValue: '••••••••',
        status: 'pending'
      });
    }

    return changes;
  };

  const handleSaveWithConfirmation = () => {
    const changes = detectChanges();
    if (changes.length === 0) {
      setErrors({ general: 'No changes detected to save.' });
      return;
    }
    
    setPendingChanges(changes);
    setShowChangesModal(true);
  };

  const handleInputChange = (field, value) => {
    setUserData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handlePasswordChange = (field, value) => {
    setPasswordData(prev => ({
      ...prev,
      [field]: value
    }));
    
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

const handleNotificationChange = async (setting) => {
  const newValue = !notifications[setting];
  
  // Optimistically update the UI
  setNotifications(prev => ({
    ...prev,
    [setting]: newValue
  }));

  try {
    let response;
    
    if (setting === 'emailNotifications') {
      if (newValue === false) {
        // Turn OFF email notifications - uses GET endpoint
        response = await fetch(`${process.env.REACT_APP_API_URL || 'https://mars2.mediacle.net'}/api/emailoff`, {
          credentials: 'include'
        });
      } else {
        // Turn ON email notifications - uses PUT endpoint
        response = await fetch(`${process.env.REACT_APP_API_URL || 'https://mars2.mediacle.net'}/api/emailoff/update-notifications`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify({
            notifications: true  // Backend expects 'notifications', not 'emailNotifications'
          })
        });
      }
      
      const data = await response.json();
      
      if (!data.success) {
        // Revert the change if the API call failed
        setNotifications(prev => ({
          ...prev,
          [setting]: !newValue
        }));
        setErrors({ general: data.message || 'Failed to update notification settings' });
      } else {
        // Clear any previous errors on success
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.general;
          return newErrors;
        });
        
        // Success feedback (optional)
        setSuccessMessage('Notification settings updated');
        setTimeout(() => setSuccessMessage(''), 3000);
      }
    }
  } catch (error) {
    console.error('Error updating notifications:', error);
    // Revert the change if there was a network error
    setNotifications(prev => ({
      ...prev,
      [setting]: !newValue
    }));
    setErrors({ general: 'Network error while updating notifications' });
  }
};

  const validateForm = () => {
    const newErrors = {};
    
    if (activeTab === 'personal') {
      if (!userData.firstName.trim()) newErrors.firstName = 'First name is required';
      if (!userData.lastName.trim()) newErrors.lastName = 'Last name is required';
      // A team member's company is the account owner's; they don't edit it.
      if (!teamMember && !userData.companyName.trim()) newErrors.companyName = 'Company name is required';
      
      // Validate sender email if provided
   
    }
    
    if (activeTab === 'security' && (passwordData.currentPassword || passwordData.newPassword || passwordData.confirmPassword)) {
      // Google users don't need current password
      if (!isGoogleUser && !passwordData.currentPassword) newErrors.currentPassword = 'Current password is required';
      if (!passwordData.newPassword) newErrors.newPassword = 'New password is required';
      if (passwordData.newPassword.length < 8) newErrors.newPassword = 'Password must be at least 8 characters';
      if (passwordData.newPassword !== passwordData.confirmPassword) newErrors.confirmPassword = 'Passwords do not match';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    
    setIsSaving(true);
    setSuccessMessage('');
    
    try {
      const isPasswordChange = activeTab === 'security' && (passwordData.currentPassword || (isGoogleUser && passwordData.newPassword));
      const endpoint = isPasswordChange
        ? '/api/users/change-password'
        : '/api/users/update-profile';

      const requestData = isPasswordChange
        ? passwordData
        : userData;
      
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://mars2.mediacle.net'}${endpoint}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(requestData)
      });
      
      const data = await response.json();
      
      if (data.success) {
        // Update pending changes to success status
        setPendingChanges(prev => prev.map(change => ({
          ...change,
          status: 'success'
        })));

        // Close modal after showing success for 1.5 seconds
        setTimeout(() => {
          setShowChangesModal(false);
          setIsEditing(false);
          
          if (activeTab === 'security') {
            setPasswordData({
              currentPassword: '',
              newPassword: '',
              confirmPassword: ''
            });
          }
          
          // Update original data
          setOriginalData({ ...userData });
          
          // Trigger header update with new user data
          if (onProfileUpdate && typeof onProfileUpdate === 'function') {
            const updatedUserData = {
              id: data.user?.id || userData.id,
              firstName: userData.firstName,
              lastName: userData.lastName,
              name: `${userData.firstName} ${userData.lastName}`.trim(),
              email: userData.businessEmail,
              companyName: userData.companyName,
              plan: userData.plan,
              senderEmail: userData.senderEmail,
              initials: `${userData.firstName?.charAt(0) || ''}${userData.lastName?.charAt(0) || ''}`.toUpperCase()
            };
            onProfileUpdate(updatedUserData);
          }
          
          setSuccessMessage(
            activeTab === 'security' 
              ? 'Password changed successfully!' 
              : 'Profile updated successfully!'
          );
          
          setTimeout(() => setSuccessMessage(''), 3000);
        }, 1500);
      } else {
        setErrors({ general: data.message || 'Failed to update profile' });
        setShowChangesModal(false);
      }
    } catch (error) {
      setErrors({ general: 'Network error. Please try again.' });
      setShowChangesModal(false);
    } finally {
      setIsSaving(false);
    }
  };

  // Company, billing and the account's notification emails belong to the
  // account owner, so team members don't get those tabs.
  const tabs = [
    { id: 'personal', label: 'Personal Info', icon: User },
    { id: 'company', label: 'Company', icon: Building },
    { id: 'billing', label: 'Billing', icon: CreditCard },
    { id: 'security', label: 'Security', icon: Shield },
    { id: 'notifications', label: 'Notifications', icon: Bell }
  ].filter((tab) => !teamMember || !['company', 'billing', 'notifications'].includes(tab.id));

  const renderPersonalTab = () => (
    <div className="space-y-6">
      {teamMember && (
        <div className="rounded-lg border border-blue-200 dark:border-blue-900 bg-blue-50 dark:bg-blue-950/30 px-4 py-3 text-sm text-blue-800 dark:text-blue-300">
          You're on the <strong>{account.accountName || account.ownerName}</strong> team as {account.role === 'co_admin' ? 'a co-admin' : 'a member'}
          {' '}({userData.plan} plan). {account.ownerName || 'The account owner'} manages billing and the company details.
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-slate-400 mb-2">First Name</label>
          <input
            type="text"
            value={userData.firstName}
            onChange={(e) => handleInputChange('firstName', e.target.value)}
            disabled={!isEditing}
            className={`w-full px-4 py-3 border rounded-lg transition-colors dark:bg-slate-800 dark:border-slate-600 dark:text-slate-100 dark:placeholder-slate-500 ${
              isEditing ? 'border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100' : 'border-gray-200 bg-gray-50 dark:bg-slate-800'
            } ${errors.firstName ? 'border-red-500' : ''}`}
          />
          {errors.firstName && <p className="text-red-500 text-sm mt-1">{errors.firstName}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-slate-400 mb-2">Last Name</label>
          <input
            type="text"
            value={userData.lastName}
            onChange={(e) => handleInputChange('lastName', e.target.value)}
            disabled={!isEditing}
            className={`w-full px-4 py-3 border rounded-lg transition-colors dark:bg-slate-800 dark:border-slate-600 dark:text-slate-100 dark:placeholder-slate-500 ${
              isEditing ? 'border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100' : 'border-gray-200 bg-gray-50 dark:bg-slate-800'
            } ${errors.lastName ? 'border-red-500' : ''}`}
          />
          {errors.lastName && <p className="text-red-500 text-sm mt-1">{errors.lastName}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-slate-400 mb-2">
            Business Email
            <span className="text-gray-500 dark:text-slate-500 text-sm ml-1">(Cannot be changed)</span>
          </label>
          <input
            type="email"
            value={userData.businessEmail}
            disabled={true} // Always disabled
            className="w-full px-4 py-3 border border-gray-200 dark:border-slate-600 bg-gray-50 dark:bg-slate-800 rounded-lg text-gray-600 dark:text-slate-400 cursor-not-allowed"
          />
          <p className="text-xs text-gray-500 dark:text-slate-500 mt-1">Contact support to change your business email</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-slate-400 mb-2">Phone Number</label>
          <input
            type="tel"
            value={userData.phone}
            onChange={(e) => handleInputChange('phone', e.target.value)}
            disabled={!isEditing}
            className={`w-full px-4 py-3 border rounded-lg transition-colors dark:bg-slate-800 dark:border-slate-600 dark:text-slate-100 dark:placeholder-slate-500 ${
              isEditing ? 'border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100' : 'border-gray-200 bg-gray-50 dark:bg-slate-800'
            }`}
          />
        </div>


      </div>
    </div>
  );

  const renderCompanyTab = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-slate-400 mb-2">Company Name</label>
          <input
            type="text"
            value={userData.companyName}
            onChange={(e) => handleInputChange('companyName', e.target.value)}
            disabled={!isEditing}
            className={`w-full px-4 py-3 border rounded-lg transition-colors dark:bg-slate-800 dark:border-slate-600 dark:text-slate-100 dark:placeholder-slate-500 ${
              isEditing ? 'border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 dark:focus:ring-blue-900' : 'border-gray-200 bg-gray-50 dark:bg-slate-800'
            } ${errors.companyName ? 'border-red-500' : ''}`}
          />
          {errors.companyName && <p className="text-red-500 text-sm mt-1">{errors.companyName}</p>}
          <p className="text-xs text-gray-500 dark:text-slate-500 mt-1">
            The business sending your outreach. Shown on your invoices.
          </p>
          {/* Matches the note under Billing Address: the send path refuses a
              message it cannot attribute to a named sender. */}
          {!userData.companyName && (
            <p className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
              Needed before you can send outreach — sending is paused until this is filled in.
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-slate-400 mb-2">Country</label>
          <input
            type="text"
            value={userData.country}
            onChange={(e) => handleInputChange('country', e.target.value)}
            disabled={!isEditing}
            className={`w-full px-4 py-3 border rounded-lg transition-colors dark:bg-slate-800 dark:border-slate-600 dark:text-slate-100 dark:placeholder-slate-500 ${
              isEditing ? 'border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 dark:focus:ring-blue-900' : 'border-gray-200 bg-gray-50 dark:bg-slate-800'
            }`}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-slate-400 mb-2">ZIP Code</label>
          <input
            type="text"
            value={userData.zipCode}
            onChange={(e) => handleInputChange('zipCode', e.target.value)}
            disabled={!isEditing}
            className={`w-full px-4 py-3 border rounded-lg transition-colors dark:bg-slate-800 dark:border-slate-600 dark:text-slate-100 dark:placeholder-slate-500 ${
              isEditing ? 'border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 dark:focus:ring-blue-900' : 'border-gray-200 bg-gray-50 dark:bg-slate-800'
            }`}
          />
        </div>
      </div>

      {/* Plan Information */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Crown className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            <h3 className="text-lg font-semibold text-gray-800 dark:text-slate-100">Current Plan</h3>
          </div>
          <span className="px-3 py-1 bg-blue-600 text-white rounded-full text-sm font-medium">
            {userData.plan}
          </span>
        </div>
        <div className="flex items-center justify-between p-3 bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700">
          <span className="text-gray-600 dark:text-slate-400">Plan Status</span>
          <span className="text-green-600 dark:text-green-400 font-medium">Active</span>
        </div>
      </div>
    </div>
  );

  const renderBillingTab = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-slate-400 mb-2">
            VAT ID
            <span className="text-gray-500 dark:text-slate-500 text-sm ml-1">(Optional)</span>
          </label>
          <input
            type="text"
            value={userData.vatId}
            onChange={(e) => handleInputChange('vatId', e.target.value)}
            disabled={!isEditing}
            placeholder="e.g., GB123456789, DE123456789, FR12345678901"
            className={`w-full px-4 py-3 border rounded-lg transition-colors dark:bg-slate-800 dark:border-slate-600 dark:text-slate-100 dark:placeholder-slate-500 ${
              isEditing ? 'border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 dark:focus:ring-blue-900' : 'border-gray-200 bg-gray-50 dark:bg-slate-800'
            }`}
          />
          <p className="text-xs text-gray-500 dark:text-slate-500 mt-1">
            Enter your VAT identification number if applicable for your region
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-slate-400 mb-2">
            Billing Address <span className="text-red-500">*</span>
          </label>
          <textarea
            value={userData.billingAddress}
            onChange={(e) => handleInputChange('billingAddress', e.target.value)}
            disabled={!isEditing}
            rows="4"
            placeholder="Enter your complete billing address including street, city, state/province, postal code, and country"
            className={`w-full px-4 py-3 border rounded-lg transition-colors dark:bg-slate-800 dark:border-slate-600 dark:text-slate-100 dark:placeholder-slate-500 ${
              isEditing ? 'border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 dark:focus:ring-blue-900' : 'border-gray-200 bg-gray-50 dark:bg-slate-800'
            }`}
          />
          <p className="text-xs text-gray-500 dark:text-slate-500 mt-1">
            Appears on your invoices.
          </p>
          {/* Sending is refused without it. It is not added to the message
              itself (see services/outreachFooter) — it is held here so the
              business behind an outreach campaign is always known. */}
          {!userData.billingAddress && (
            <p className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
              Required before you can send outreach — sending is paused until this is filled in.
            </p>
          )}
        </div>
      </div>

      {/* Billing Information Note */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <CreditCard className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" />
          <div>
            <h4 className="text-sm font-medium text-blue-800 dark:text-blue-300 mb-1">Billing Information</h4>
            <p className="text-xs text-blue-700 dark:text-blue-400">
              This information will be used for invoices and billing purposes.
              Keep it updated to ensure accurate billing and compliance with tax regulations.
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderSecurityTab = () => (
    <div className="space-y-6">
      {isGoogleUser ? (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            <p className="text-sm text-blue-800 dark:text-blue-300">
              You signed up with Google. Set a password below if you'd also like to login with email and password.
            </p>
          </div>
        </div>
      ) : (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
            <p className="text-sm text-yellow-800 dark:text-yellow-300">
              Leave password fields empty if you don't want to change your password.
            </p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-6">
        {!isGoogleUser && (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-slate-400 mb-2">Current Password</label>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              value={passwordData.currentPassword}
              onChange={(e) => handlePasswordChange('currentPassword', e.target.value)}
              disabled={!isEditing}
              className={`w-full px-4 py-3 pr-12 border rounded-lg transition-colors dark:bg-slate-800 dark:border-slate-600 dark:text-slate-100 ${
                isEditing ? 'border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 dark:focus:ring-blue-900' : 'border-gray-200 bg-gray-50 dark:bg-slate-800'
              } ${errors.currentPassword ? 'border-red-500' : ''}`}
            />
            {isEditing && (
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-slate-300"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            )}
          </div>
          {errors.currentPassword && <p className="text-red-500 text-sm mt-1">{errors.currentPassword}</p>}
        </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-slate-400 mb-2">New Password</label>
          <div className="relative">
            <input
              type={showNewPassword ? 'text' : 'password'}
              value={passwordData.newPassword}
              onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
              disabled={!isEditing}
              className={`w-full px-4 py-3 pr-12 border rounded-lg transition-colors dark:bg-slate-800 dark:border-slate-600 dark:text-slate-100 ${
                isEditing ? 'border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 dark:focus:ring-blue-900' : 'border-gray-200 bg-gray-50 dark:bg-slate-800'
              } ${errors.newPassword ? 'border-red-500' : ''}`}
            />
            {isEditing && (
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-slate-300"
              >
                {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            )}
          </div>
          {errors.newPassword && <p className="text-red-500 text-sm mt-1">{errors.newPassword}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-slate-400 mb-2">Confirm New Password</label>
          <div className="relative">
            <input
              type={showConfirmPassword ? 'text' : 'password'}
              value={passwordData.confirmPassword}
              onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
              disabled={!isEditing}
              className={`w-full px-4 py-3 pr-12 border rounded-lg transition-colors dark:bg-slate-800 dark:border-slate-600 dark:text-slate-100 ${
                isEditing ? 'border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 dark:focus:ring-blue-900' : 'border-gray-200 bg-gray-50 dark:bg-slate-800'
              } ${errors.confirmPassword ? 'border-red-500' : ''}`}
            />
            {isEditing && (
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-slate-300"
              >
                {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            )}
          </div>
          {errors.confirmPassword && <p className="text-red-500 text-sm mt-1">{errors.confirmPassword}</p>}
        </div>
      </div>
    </div>
  );

   const renderNotificationsTab = () => (
  <div className="space-y-6">
    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
      <div className="flex items-center gap-2">
        <Bell className="w-5 h-5 text-blue-600 dark:text-blue-400" />
        <p className="text-sm text-blue-800 dark:text-blue-300">
          Notification settings are updated automatically when you toggle them.
        </p>
      </div>
    </div>

    <div className="space-y-4">
      {Object.entries(notifications).map(([key, value]) => (
        <div key={key} className="flex items-center justify-between p-4 border border-gray-200 dark:border-slate-700 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors">
          <div>
            <h4 className="font-medium text-gray-800 dark:text-slate-100 capitalize">
              {key.replace(/([A-Z])/g, ' $1').trim()}
            </h4>
            <p className="text-sm text-gray-600 dark:text-slate-400">
              {key === 'emailNotifications' && 'Receive email notifications for important updates'}
            </p>
          </div>
          {value === null ? (
            <Loader2 className="w-5 h-5 animate-spin text-gray-400 dark:text-slate-500" />
          ) : (
            <button
              onClick={() => handleNotificationChange(key)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-slate-900 ${
                value ? 'bg-blue-600' : 'bg-gray-200 dark:bg-slate-600'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow-lg ${
                  value ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          )}
        </div>
      ))}
    </div>
  </div>
);

  const renderTabContent = () => {
    switch (activeTab) {
      case 'personal':
        return renderPersonalTab();
      case 'company':
        return teamMember ? renderPersonalTab() : renderCompanyTab();
      case 'billing':
        return teamMember ? renderPersonalTab() : renderBillingTab();
      case 'security':
        return renderSecurityTab();
      case 'notifications':
        return renderNotificationsTab();
      default:
        return renderPersonalTab();
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-950">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-800 dark:text-slate-100">Profile Settings</h1>
              <p className="text-gray-600 dark:text-slate-400 mt-1">Manage your account information and preferences</p>
            </div>
            <div className="flex items-center gap-3">
              {!isEditing ? (
                <button
                  onClick={() => setIsEditing(true)}
                  className="flex items-center gap-2 px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm font-medium"
                >
                  <Edit3 className="w-4 h-4" />
                  Edit Profile
                </button>
              ) : (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      setIsEditing(false);
                      setErrors({});
                      setPasswordData({
                        currentPassword: '',
                        newPassword: '',
                        confirmPassword: ''
                      });
                      setUserData({ ...originalData });
                    }}
                    className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-slate-300 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors text-sm"
                  >
                    <X className="w-4 h-4" />
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveWithConfirmation}
                    disabled={isSaving}
                    className="flex items-center gap-2 px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 text-sm font-medium"
                  >
                    {isSaving ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4" />
                    )}
                    {isSaving ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Success/Error Messages */}
          {successMessage && (
            <div className="mt-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg flex items-center gap-2">
              <Check className="w-5 h-5 text-green-600 dark:text-green-400" />
              <span className="text-green-800 dark:text-green-300 text-sm">{successMessage}</span>
            </div>
          )}

          {errors.general && (
            <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
              <span className="text-red-800 dark:text-red-300 text-sm">{errors.general}</span>
            </div>
          )}
        </div>

        {/* Tabs Navigation */}
        <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 mb-6">
          <div className="px-6 py-4">
            <nav className="flex space-x-8" aria-label="Tabs">
              {tabs.map((tab, index) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`relative flex items-center gap-3 px-1 py-3 text-sm font-medium transition-colors focus:outline-none ${
                    activeTab === tab.id
                      ? 'text-blue-600 dark:text-blue-400'
                      : 'text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-300'
                  }`}
                >
                  {/* Circle indicator */}
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                    activeTab === tab.id
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 dark:bg-slate-700 text-gray-500 dark:text-slate-400'
                  }`}>
                    <tab.icon className="w-4 h-4" />
                  </div>
                  
                  <span>{tab.label}</span>
                  
                  {/* Active tab indicator line */}
                  {activeTab === tab.id && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 rounded-full" />
                  )}
                  
                  {/* Connection line to next tab */}
                  {index < tabs.length - 1 && (
                    <div  />
                  )}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Main Content */}
        <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 p-6">
          {renderTabContent()}
        </div>

        {/* Changes Confirmation Modal */}
        {showChangesModal && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl max-w-md w-full max-h-[80vh] overflow-hidden border border-gray-200 dark:border-slate-700">
              {/* Modal Header */}
              <div className="px-6 py-4 border-b border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800/50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
                      <Save className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-800 dark:text-slate-100">Confirm Changes</h3>
                      <p className="text-sm text-gray-600 dark:text-slate-400">Review your updates before saving</p>
                    </div>
                  </div>
                  {!isSaving && (
                    <button
                      onClick={() => setShowChangesModal(false)}
                      className="p-1 hover:bg-gray-200 dark:hover:bg-slate-700 rounded-md transition-colors"
                    >
                      <X className="w-5 h-5 text-gray-600 dark:text-slate-400" />
                    </button>
                  )}
                </div>
              </div>

              {/* Modal Body */}
              <div className="px-6 py-4 max-h-60 overflow-y-auto">
                <div className="space-y-3">
                  {pendingChanges.map((change, index) => (
                    <div
                      key={index}
                      className={`flex items-center justify-between p-3 rounded-lg border transition-all duration-300 ${
                        change.status === 'success'
                          ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                          : 'bg-gray-50 dark:bg-slate-800 border-gray-200 dark:border-slate-700'
                      }`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-gray-800 dark:text-slate-100 text-sm">{change.label}</span>
                          <div className={`w-5 h-5 rounded-full flex items-center justify-center transition-all duration-500 ${
                            change.status === 'success'
                              ? 'bg-green-600 scale-110'
                              : 'bg-blue-600 animate-pulse'
                          }`}>
                            {change.status === 'success' ? (
                              <Check className="w-3 h-3 text-white" />
                            ) : (
                              <div className="w-2 h-2 bg-white rounded-full"></div>
                            )}
                          </div>
                        </div>
                        <div className="text-xs text-gray-600 dark:text-slate-400">
                          <div className="flex items-center gap-2">
                            <span className="text-red-600 dark:text-red-400">From:</span>
                            <span className="truncate max-w-32">{change.oldValue}</span>
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-green-600 dark:text-green-400">To:</span>
                            <span className="truncate max-w-32 font-medium">{change.newValue}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Modal Footer */}
              <div className="px-6 py-4 border-t border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800/50">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-600 dark:text-slate-400">
                    {pendingChanges.filter(c => c.status === 'success').length} of {pendingChanges.length} changes
                    {pendingChanges.every(c => c.status === 'success') ? ' completed' : ' pending'}
                  </div>
                  <div className="flex items-center gap-3">
                    {!isSaving && !pendingChanges.every(c => c.status === 'success') && (
                      <button
                        onClick={() => setShowChangesModal(false)}
                        className="px-4 py-2 border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-slate-300 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors text-sm"
                      >
                        Cancel
                      </button>
                    )}
                    {!pendingChanges.every(c => c.status === 'success') && (
                      <button
                        onClick={handleSave}
                        disabled={isSaving}
                        className="flex items-center gap-2 px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 text-sm"
                      >
                        {isSaving ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            <span>Saving...</span>
                          </>
                        ) : (
                          <>
                            <Save className="w-4 h-4" />
                            <span>Save All Changes</span>
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfilePage; 